import { QuestionTemplate, QuestionCategory, Country, GameData, GameMode } from '../types';
import { TRIVIA_QUESTIONS } from './triviaQuestionBank';

const VOWELS = ['A', 'E', 'I', 'O', 'U'];
const CURRENCY_CODE_LABELS: Record<string, string> = {
  EUR: 'euro',
  USD: 'US dollar',
  AUD: 'Australian dollar',
  NZD: 'New Zealand dollar',
  XOF: 'West African CFA franc',
  XAF: 'Central African CFA franc',
  XCD: 'East Caribbean dollar',
};
const CURRENCY_GROUP_LABELS: Record<string, string> = {
  pound: 'pound',
  dollar: 'dollar',
  peso: 'peso',
  dinar: 'dinar',
  'rial/riyal': 'rial/riyal',
  franc: 'franc',
};
const FRACTIONAL_UNIT_LABELS: Record<string, string> = {
  cent: 'cents',
  pence: 'pence',
  centavo: 'centavos',
  sen: 'sen',
};

const countKnownBorders = (country: Country, data: GameData): number =>
  country.borders.filter(border => Boolean(data.indexes.byIso3[border])).length;

const getCountryNameLength = (name: string): number => name.replace(/[^a-z]/gi, '').length;

const getLengthBuckets = (countries: Country[]): Record<number, Country[]> =>
  countries.reduce<Record<number, Country[]>>((acc, country) => {
    const length = getCountryNameLength(country.name);
    if (!acc[length]) acc[length] = [];
    acc[length].push(country);
    return acc;
  }, {});

// Phase 4: Templates

export const TEMPLATES: QuestionTemplate[] = [
  // --- NAMING ---
  {
    id: 'starts_with',
    category: QuestionCategory.NAMING,
    getParams: (data) => {
      // Pick a random letter that has at least 3 countries
      const letters = Object.keys(data.indexes.byFirstLetter).filter(l => data.indexes.byFirstLetter[l].length >= 3);
      if (letters.length === 0) return undefined; // Return undefined to trigger retry, not null
      return letters[Math.floor(Math.random() * letters.length)];
    },
    template: (p) => `Name a country starting with the letter ${p}`,
    isValid: (target, answer) => answer.name.toUpperCase().startsWith(target as unknown as string),
  },
  {
    id: 'ends_with_ia',
    category: QuestionCategory.NAMING,
    getParams: () => 'ia',
    template: () => `Name a country ending in "ia"`,
    isValid: (_, answer) => answer.name.toLowerCase().endsWith('ia'),
  },
  {
    id: 'starts_with_vowel',
    category: QuestionCategory.NAMING,
    getParams: () => null,
    template: () => `Name a country starting with a vowel`,
    isValid: (_, answer) => VOWELS.includes(answer.name.charAt(0).toUpperCase()),
  },
  {
    id: 'long_name',
    category: QuestionCategory.NAMING,
    getParams: () => 10,
    template: (p) => `Name a country with at least ${p} letters`,
    isValid: (target, answer) =>
      getCountryNameLength(answer.name) >= (target as unknown as number),
  },
  {
    id: 'exact_length',
    category: QuestionCategory.NAMING,
    getParams: (data) => {
      const buckets = getLengthBuckets(data.countries);
      const lengths = Object.keys(buckets)
        .map(Number)
        .filter(length => buckets[length].length >= 3);
      if (lengths.length === 0) return undefined;
      return lengths[Math.floor(Math.random() * lengths.length)];
    },
    template: (p) => `Name a country with exactly ${p} letters`,
    isValid: (target, answer) =>
      getCountryNameLength(answer.name) === (target as unknown as number),
  },
  {
    id: 'short_name',
    category: QuestionCategory.NAMING,
    getParams: (data) => {
      const buckets = getLengthBuckets(data.countries);
      const lengths = Object.keys(buckets)
        .map(Number)
        .filter(length => length <= 6 && buckets[length].length >= 2);
      if (lengths.length === 0) return undefined;
      return lengths[Math.floor(Math.random() * lengths.length)];
    },
    template: (p) => `Name a country with ${p} letters`,
    isValid: (target, answer) =>
      getCountryNameLength(answer.name) === (target as unknown as number),
  },
  {
    id: 'same_first_letter_as',
    category: QuestionCategory.NAMING,
    getParams: (data) => data.countries[Math.floor(Math.random() * data.countries.length)],
    template: (c) => `Name a country that starts with the same letter as ${c?.name}`,
    isValid: (target, answer) =>
      answer.name.charAt(0).toUpperCase() === (target as Country).name.charAt(0).toUpperCase(),
  },

  // --- CAPITAL ---
  {
    id: 'capital_is',
    category: QuestionCategory.CAPITAL,
    tags: ['capital', 'single-answer'],
    getParams: (data) => {
      const capitals = Object.keys(data.indexes.byCapital);
      if (capitals.length === 0) return undefined;
      return capitals[Math.floor(Math.random() * capitals.length)];
    },
    template: (capital) => `Name the country whose capital is ${capital}`,
    isValid: (target, answer) => answer.capital === (target as unknown as string),
    getEvidence: (capital) => ({
      kind: 'capital',
      label: 'Capital city',
      value: String(capital),
    }),
  },
  {
    id: 'capital_starts_with',
    category: QuestionCategory.CAPITAL,
    tags: ['capital'],
    getParams: (data) => {
      const capitals = data.countries.map((country) => country.capital).filter(Boolean) as string[];
      const letters = Array.from(new Set(capitals.map((capital) => capital.charAt(0).toUpperCase())))
        .filter((letter) => capitals.filter((capital) => capital.toUpperCase().startsWith(letter)).length >= 3);
      if (letters.length === 0) return undefined;
      return letters[Math.floor(Math.random() * letters.length)];
    },
    template: (letter) => `Name a country with a capital starting with ${letter}`,
    isValid: (target, answer) =>
      !!answer.capital && answer.capital.toUpperCase().startsWith(String(target).toUpperCase()),
  },

  // --- REGION ---
  {
    id: 'in_region',
    category: QuestionCategory.REGION,
    getParams: (data) => {
      const regions = Object.keys(data.indexes.byRegion);
      if (regions.length === 0) return undefined;
      return regions[Math.floor(Math.random() * regions.length)];
    },
    template: (p) => `Name a country in ${p}`,
    isValid: (target, answer) => answer.region === (target as unknown as string),
  },

  // --- BORDERS ---
  {
    id: 'borders_country',
    category: QuestionCategory.BORDERS,
    getParams: (data) => {
      // Pick a country that has borders
      const candidates = data.countries.filter(c => c.borders.some(border => Boolean(data.indexes.byIso3[border])));
      if (candidates.length === 0) return undefined;
      return candidates[Math.floor(Math.random() * candidates.length)];
    },
    template: (c) => `Name a country that borders ${c?.name}`,
    isValid: (target, answer) => target.borders.includes(answer.iso3),
  },
  {
    id: 'has_no_borders',
    category: QuestionCategory.BORDERS,
    getParams: () => null, // Explicit null is valid for this template (no subject)
    template: () => `Name a country with NO land borders (Island/Enclave)`,
    isValid: (_, answer) => answer.borders.length === 0,
  },
  {
    id: 'many_borders',
    category: QuestionCategory.BORDERS,
    getParams: () => 4,
    template: (p) => `Name a country with at least ${p} land borders`,
    isValid: (target, answer) => answer.borders.length >= (target as unknown as number),
  },
  {
    id: 'shares_border_with',
    category: QuestionCategory.BORDERS,
    getParams: (data) => {
      const candidates = data.countries.filter(c => countKnownBorders(c, data) > 0);
      if (candidates.length === 0) return undefined;
      return candidates[Math.floor(Math.random() * candidates.length)];
    },
    template: (c) => `Name a country that shares at least one border with ${c?.name}`,
    isValid: (target, answer) => {
      const subject = target as Country;
      return answer.borders.some(border => border === subject.iso3);
    },
  },

  // --- CURRENCY ---
  {
    id: 'uses_currency',
    category: QuestionCategory.CURRENCY,
    getParams: (data) => {
      const codes = Object.keys(data.indexes.byCurrencyCode).filter((code) => Boolean(CURRENCY_CODE_LABELS[code]));
      if (codes.length === 0) return undefined;
      return codes[Math.floor(Math.random() * codes.length)];
    },
    template: (code) => `Name a country that uses the ${CURRENCY_CODE_LABELS[code as string]}`,
    isValid: (target, answer) => answer.currencyCodes.includes(target as string),
  },
  {
    id: 'currency_name_group',
    category: QuestionCategory.CURRENCY,
    getParams: (data) => {
      const groups = Object.keys(data.indexes.byCurrencyGroup).filter((group) => Boolean(CURRENCY_GROUP_LABELS[group]));
      if (groups.length === 0) return undefined;
      return groups[Math.floor(Math.random() * groups.length)];
    },
    template: (group) => `Name a country whose currency is called a ${CURRENCY_GROUP_LABELS[group as string]}`,
    isValid: (target, answer) => answer.currencyGroupNames.includes(target as string),
  },
  {
    id: 'no_own_currency',
    category: QuestionCategory.CURRENCY,
    getParams: () => null,
    template: () => `Name a country that does not issue its own currency`,
    isValid: (_, answer) => answer.issuesOwnCurrency === false,
  },
  {
    id: 'fractional_unit',
    category: QuestionCategory.CURRENCY,
    getParams: (data) => {
      const units = Object.keys(data.indexes.byFractionalUnit).filter((unit) => Boolean(FRACTIONAL_UNIT_LABELS[unit]));
      if (units.length === 0) return undefined;
      return units[Math.floor(Math.random() * units.length)];
    },
    template: (unit) => `Name a country whose currency is divided into ${FRACTIONAL_UNIT_LABELS[unit as string]}`,
    isValid: (target, answer) => answer.fractionalUnits.includes(target as string),
  },
  {
    id: 'no_fractional_unit',
    category: QuestionCategory.CURRENCY,
    getParams: () => null,
    template: () => `Name a country whose currency has no fractional unit in use`,
    isValid: (_, answer) => answer.fractionalUnitInUse === false,
  },
  
  // --- FLAG ---
  {
    id: 'flag_identify',
    category: QuestionCategory.FLAG,
    tags: ['flag', 'visual', 'single-answer'],
    getParams: (data) => data.countries[Math.floor(Math.random() * data.countries.length)],
    template: () => 'Which country does this flag belong to?',
    isValid: (target, answer) => (target as Country).iso3 === answer.iso3,
    getEvidence: (country) => ({
      kind: 'flag',
      label: (country as Country)?.name ?? undefined,
      url: (country as Country)?.flagUrl ?? undefined,
    }),
  },
];

const TRIVIA_TEMPLATES: QuestionTemplate[] = TRIVIA_QUESTIONS.map((question) => ({
  id: `trivia_${question.id}`,
  category: QuestionCategory.TRIVIA,
  getParams: (data) => {
    const answers = data.indexes.byTriviaTag[question.id] ?? [];
    if (answers.length === 0) return undefined;
    return question.id;
  },
  template: () => question.question,
  isValid: () => false,
}));

TEMPLATES.push(...TRIVIA_TEMPLATES);

type TemplateFilters = {
  mode?: GameMode;
  includeCategories?: QuestionCategory[];
  excludeCategories?: QuestionCategory[];
  includeTags?: string[];
  excludeTags?: string[];
};

const isTemplateAllowed = (template: QuestionTemplate, filters?: TemplateFilters): boolean => {
  if (!filters) return true;
  if (filters.mode) {
    if (template.modeInclude && !template.modeInclude.includes(filters.mode)) {
      return false;
    }
    if (template.modeExclude && template.modeExclude.includes(filters.mode)) {
      return false;
    }
  }
  if (filters.includeCategories && !filters.includeCategories.includes(template.category)) {
    return false;
  }
  if (filters.excludeCategories && filters.excludeCategories.includes(template.category)) {
    return false;
  }
  if (filters.includeTags) {
    const templateTags = template.tags ?? [];
    if (!filters.includeTags.some((tag) => templateTags.includes(tag))) {
      return false;
    }
  }
  if (filters.excludeTags) {
    const templateTags = template.tags ?? [];
    if (filters.excludeTags.some((tag) => templateTags.includes(tag))) {
      return false;
    }
  }
  return true;
};

export const getRandomTemplate = (
  lastCategory: QuestionCategory | null,
  data: GameData,
  filters?: TemplateFilters
): { template: QuestionTemplate; params: any } => {
  // Filter available templates
  let candidates = TEMPLATES.filter((template) => isTemplateAllowed(template, filters));
  
  // Cooldown logic: Don't repeat category immediately if possible
  if (lastCategory && candidates.length > 1) {
    const filtered = candidates.filter(t => t.category !== lastCategory);
    // Ensure we have candidates left after filtering
    if (filtered.length > 0) candidates = filtered;
  }

  const template = candidates[Math.floor(Math.random() * candidates.length)];
  const params = template.getParams(data);

  // If params generation failed (e.g. not enough data), retry recursively (simple safety)
  // Check for undefined specifically; null is a valid param for some templates (e.g. has_no_borders)
  if (params === undefined) {
    return getRandomTemplate(lastCategory, data, filters);
  }

  return { template, params };
};
